import React, { useState, useEffect } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import {
  Platform,
  useGetListeningTopicFeedLazyQuery,
  useMarkTopicContentInteractedMutation,
  useHideTopicContentMutation,
} from 'graphql/generated/graphql'
import ContentFeed from 'pages/DiscoverContent/components/ContentFeed'

const platformIcons: Record<Platform, string> = {
  [Platform.Instagram]: '📷',
  [Platform.TikTok]: '🎵',
  [Platform.Youtube]: '🎥',
  [Platform.Reddit]: '🔴',
  [Platform.Facebook]: '📘',
  [Platform.Twitter]: '🐦',
  [Platform.Bluesky]: '🦋',
}

const platformGradients: Record<Platform, string> = {
  [Platform.Instagram]: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
  [Platform.TikTok]: 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)',
  [Platform.Youtube]: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
  [Platform.Reddit]: 'linear-gradient(135deg, #ff4500 0%, #cc3700 100%)',
  [Platform.Facebook]: 'linear-gradient(135deg, #1877f2 0%, #0c5ec9 100%)',
  [Platform.Twitter]: 'linear-gradient(135deg, #1da1f2 0%, #0d8ac9 100%)',
  [Platform.Bluesky]: 'linear-gradient(135deg, #0085ff 0%, #0066cc 100%)',
}

interface PlatformColumnProps {
  topicId: string
  platform: Platform
  refreshKey?: number // bump to trigger refetch
}

/**
 * A single vertical column showing a feed for one platform within a topic.
 * Used in the side-by-side multi-platform view.
 */
export default function PlatformColumn({ topicId, platform, refreshKey }: PlatformColumnProps): JSX.Element {
  const [content, setContent] = useState<any[]>([])
  const [fetchFeed, { loading }] = useGetListeningTopicFeedLazyQuery()
  const [markInteracted] = useMarkTopicContentInteractedMutation()
  const [hideContent] = useHideTopicContentMutation()

  const loadContent = async (): Promise<void> => {
    const result = await fetchFeed({
      variables: {
        input: {
          topicId,
          platform,
          limit: 50,
          offset: 0,
        },
      },
      fetchPolicy: 'network-only',
    })
    if (result.data?.getListeningTopicFeed) {
      setContent(result.data.getListeningTopicFeed.content)
    }
  }

  useEffect(() => {
    loadContent().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, platform, refreshKey])

  const handleInteract = (contentId: string): void => {
    setContent((prev) => {
      const updated = prev.map((c) =>
        c.id === contentId ? { ...c, interactedWith: true } : c
      )
      return [...updated].sort((a, b) => {
        if (a.interactedWith !== b.interactedWith) return a.interactedWith ? 1 : -1
        return (b.similarity ?? 0) - (a.similarity ?? 0)
      })
    })
    markInteracted({ variables: { contentId } }).catch(console.error)
  }

  const handleHide = (contentId: string): void => {
    setContent((prev) => prev.filter((c) => c.id !== contentId))
    hideContent({ variables: { contentId } }).catch(console.error)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 320,
        maxWidth: 480,
        height: 'calc(100vh - 220px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      {/* Fixed column header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          background: 'rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            background: platformGradients[platform],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          {platformIcons[platform]}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>
            {platform}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', lineHeight: 1.2 }}>
            {content.length} {content.length === 1 ? 'post' : 'posts'}
          </Typography>
        </Box>
      </Box>

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(168, 85, 247, 0.3)',
            borderRadius: '4px',
          },
        }}
      >
        {loading && content.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#a855f7' }} />
          </Box>
        ) : content.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', textAlign: 'center', py: 4 }}>
            No content yet
          </Typography>
        ) : (
          // Force single-column layout inside the narrow vertical column
          <Box sx={{ '& > div': { columnCount: '1 !important' } }}>
            <ContentFeed content={content} onInteract={handleInteract} onHide={handleHide} />
          </Box>
        )}
      </Box>
    </Box>
  )
}
